import type { OffsetPaginationMeta } from "@/types/pagination";

/**
 * Which patients the workspace list is asking about. The ledger's type tabs
 * answered "what kind of document"; these answer "whose money needs me".
 */
export const BILLING_PATIENT_VIEW = {
  OWES: "owes",
  CREDIT: "credit",
  ALL: "all",
  /** Client-side only: the review queue is its own endpoint and row shape. */
  REVIEW: "review",
  /** Client-side only: the flat document ledger, for reconciling by number. */
  DOCUMENTS: "documents",
} as const;

export type BillingPatientView =
  (typeof BILLING_PATIENT_VIEW)[keyof typeof BILLING_PATIENT_VIEW];

/** The three the server accepts; `review` and `documents` never reach it. */
export type BillingPatientListView = Exclude<
  BillingPatientView,
  "review" | "documents"
>;

export const BILLING_PATIENT_SORT = {
  OWED: "owed",
  OLDEST: "oldest",
  CREDIT: "credit",
  RECENT: "recent",
} as const;

export type BillingPatientSort =
  (typeof BILLING_PATIENT_SORT)[keyof typeof BILLING_PATIENT_SORT];

export type BillingPatientFilter = {
  page?: number;
  limit?: number;
  view?: BillingPatientListView;
  sort?: BillingPatientSort;
  search?: string;
};

/** Every figure is a numeric string ("1512.00"), never a float. */
export type BillingPatientRow = {
  contactId: string;
  contactName: string | null;
  phone: string | null;
  invoiced: string;
  outstanding: string;
  balance: string;
  collected: string;
  /** YYYY-MM-DD. How stale the debt is. */
  oldestUnpaidDate: string | null;
  lastBillingDate: string | null;
  lastVisitAt: string | null;
  lastVisitTreatment: string | null;
  lastVisitDoctor: string | null;
};

export type BillingPatientCounts = {
  owes: number;
  credit: number;
  all: number;
  review: number;
};

export type BillingPatientsSummary = {
  owed: string;
  owedPatientCount: number;
  oldestUnpaidDate: string | null;
  collected: string;
  collectedPrevious: string;
  creditHeld: string;
  creditPatientCount: number;
  counts: BillingPatientCounts;
};

export type BillingPatientsPage = {
  result: BillingPatientRow[];
  meta: OffsetPaginationMeta;
  summary: BillingPatientsSummary;
};

export const BILLING_REVIEW_KIND = {
  HELD_INVOICE: "held_invoice",
  UNBILLED_VISIT: "unbilled_visit",
} as const;

export type BillingReviewKind =
  (typeof BILLING_REVIEW_KIND)[keyof typeof BILLING_REVIEW_KIND];

/** One thing the ledger cannot settle by itself. Fields the other kind has no answer for are null. */
export type BillingReviewRow = {
  id: string;
  kind: BillingReviewKind;
  contactId: string | null;
  contactName: string | null;
  amount: string | null;
  code: string | null;
  externalCode: string | null;
  date: string | null;
  duplicateOfCode: string | null;
  duplicateOfDate: string | null;
  appointmentId: string | null;
  treatmentName: string | null;
  doctorName: string | null;
};

export type BillingReviewFilter = {
  page?: number;
  limit?: number;
};

export type BillingReviewPage = {
  result: BillingReviewRow[];
  meta: OffsetPaginationMeta;
};

/** A patient row's strings, settled so the table cell only prints. */
export type BillingPatientDisplay = {
  phone: string | null;
  owesLabel: string | null;
  ageLabel: string | null;
  /** Debt this old is the reason someone opened the page. */
  isStale: boolean;
  creditLabel: string | null;
  /** Set only when the credit on account covers everything owed. */
  creditNote: string | null;
  lastVisitLabel: string | null;
  lastVisitDetail: string | null;
};

/** A row of the patients table, with its strings settled so the cell only prints. */
export type BillingPatientTableRow = {
  id: string;
  patient: BillingPatientRow;
  display: BillingPatientDisplay;
};
